import { createTheme } from "@mui/material/styles";
import type {} from "@mui/x-date-pickers/themeAugmentation";
import { createElement } from "react";

import { breakpoints as tokenBreakpoints } from "./design-tokens/breakpoints";
import {
  materialColors,
  materialColorsOpacity,
} from "./design-tokens/material-colors";
import { palette as tokenPalette } from "./design-tokens/palette";
import { shape as tokenShape } from "./design-tokens/shape";
import { spacing as tokenSpacing } from "./design-tokens/spacing";
import { typography as tokenTypography } from "./design-tokens/typography";

import {
  CancelIcon,
  CheckCircleOutlineIcon,
  CloseIcon,
  ErrorOutlineIcon,
  ExpandMoreIcon,
  InfoOutlinedIcon,
  WarningAmberIcon,
  CheckBoxIcon,
  IndeterminateCheckBoxIcon,
  CheckBoxOutlineBlankIcon,
} from "@/icons";

// Extend the Chip component's variant prop to include our custom variant
declare module "@mui/material/Chip" {
  interface ChipPropsVariantOverrides {
    lightGray: true;
  }
}

// Add custom theme values to our type
// https://mui.com/material-ui/customization/typography/#adding-amp-disabling-variants
declare module "@mui/material/styles" {
  interface BreakpointOverrides {
    s: true;
  }

  interface TypeText {
    contrastText: string;
    customStateColor: typeof tokenPalette.text._states;
  }

  interface TypeTextOptions {
    contrastText?: string;
    customStateColor?: typeof tokenPalette.text._states;
  }

  interface TypeBackground {
    "default-elevation": string;
    "paper-elevation-0": string;
    "paper-elevation-1": string;
    "paper-elevation-2": string;
    "paper-elevation-3": string;
    "paper-elevation-4": string;
    "paper-elevation-5": string;
    "paper-elevation-6": string;
    "paper-elevation-7": string;
    "paper-elevation-8": string;
    "paper-elevation-9": string;
    "paper-elevation-10": string;
    "paper-elevation-11": string;
    "paper-elevation-12": string;
    "paper-elevation-reversed": string;
    "paper-depression": string;
    shround: string;
  }

  interface TypeBackgroundOptions {
    "default-elevation"?: string;
    "paper-elevation-0"?: string;
    "paper-elevation-1"?: string;
    "paper-elevation-2"?: string;
    "paper-elevation-3"?: string;
    "paper-elevation-4"?: string;
    "paper-elevation-5"?: string;
    "paper-elevation-6"?: string;
    "paper-elevation-7"?: string;
    "paper-elevation-8"?: string;
    "paper-elevation-9"?: string;
    "paper-elevation-10"?: string;
    "paper-elevation-11"?: string;
    "paper-elevation-12"?: string;
    "paper-elevation-reversed"?: string;
    "paper-depression"?: string;
    shround?: string;
  }

  interface CommonColors {
    customBlackStateColors: typeof tokenPalette.common.black_states;
    customWhiteStateColors: typeof tokenPalette.common.white_states;
  }

  interface CommonColorsOptions {
    customBlackStateColors?: typeof tokenPalette.common.black_states;
    customWhiteStateColors?: typeof tokenPalette.common.white_states;
  }

  interface PaletteColor {
    bgContainer?: string;
    customStateColor?: Record<string, string>;
  }

  interface SimplePaletteColorOptions {
    bgContainer?: string;
    customStateColor?: Record<string, string>;
  }

  interface TypographyVariants {
    body3: React.CSSProperties;
    subtitle3: React.CSSProperties;
    button2: React.CSSProperties;
    chipLabel: React.CSSProperties;
    fontWeightScale: typeof tokenTypography.fontWeight;
    fontSizeScale: typeof tokenTypography.fontSize;
  }

  // allow configuration using `createTheme()`
  interface TypographyVariantsOptions {
    body3?: React.CSSProperties;
    subtitle3?: React.CSSProperties;
    button2?: React.CSSProperties;
    chipLabel?: React.CSSProperties;
    fontWeightScale?: typeof tokenTypography.fontWeight;
    fontSizeScale?: typeof tokenTypography.fontSize;
  }

  interface Shape {
    borderRadiusSm: number;
    borderRadiusMd: number;
    borderRadiusLg: number;
    borderRadiusButtonCorner: number;
  }

  interface ShapeOptions {
    borderRadiusSm?: number;
    borderRadiusMd?: number;
    borderRadiusLg?: number;
    borderRadiusButtonCorner?: number;
  }

  interface Palette {
    alert: {
      info: string;
      success: string;
      warning: string;
      error: string;
    };
    tokens: {
      common: typeof tokenPalette.common;
      outlines: typeof tokenPalette.outlines;
      elevation: typeof tokenPalette.elevation;
      icon: typeof tokenPalette.icon;
    };
    seedType: Record<
      "product" | "brand" | "photography" | "composition" | "scene" | "casting",
      { main: string; contrastText: string }
    >;
  }

  interface PaletteOptions {
    alert?: {
      info?: string;
      success?: string;
      warning?: string;
      error?: string;
    };
    tokens?: {
      common?: typeof tokenPalette.common;
      outlines?: typeof tokenPalette.outlines;
      elevation?: typeof tokenPalette.elevation;
      icon?: typeof tokenPalette.icon;
    };
    seedType?: Partial<
      Record<
        | "product"
        | "brand"
        | "photography"
        | "composition"
        | "scene"
        | "casting",
        { main: string; contrastText: string }
      >
    >;
  }
}

// Update the Typography's variant prop options
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    body3: true;
    subtitle3: true;
    button2: true;
  }
}

// Extend the LinearProgress component's variant prop to include a custom variant
declare module "@mui/material/LinearProgress" {
  interface LinearProgressPropsVariantOverrides {
    orion: true;
  }
}

// Extend the IconButton component's variant prop to include a custom variant
declare module "@mui/material/IconButton" {
  interface IconButtonPropsVariantOverrides {
    outlined: true;
  }
}

// Extend the Skeleton component's variant prop to include our custom variant
declare module "@mui/material/Skeleton" {
  interface SkeletonPropsVariantOverrides {
    queen: true;
  }
}

const typographyFamily = `${tokenTypography.fontFamily}, Arial, sans-serif`;
const typographyFontSize = tokenTypography.fontSize;
const typographyFontWeight = tokenTypography.fontWeight;
const pickerBorderColor = materialColorsOpacity.onyx["50-50%"];
const pickerContainerBackground = tokenPalette.background["paper-elevation-1"];

const orionDesignSystemTheme = createTheme({
  // Breakpoints Best practice object with MUI
  breakpoints: {
    values: {
      xs: tokenBreakpoints.xs,
      s: tokenBreakpoints.s,
      sm: tokenBreakpoints.sm,
      md: tokenBreakpoints.md,
      lg: tokenBreakpoints.lg,
      xl: tokenBreakpoints.xl,
    },
  },
  palette: {
    common: {
      black: materialColors.onyx[600],
      white: materialColors.onyx[50],
      customBlackStateColors: tokenPalette.common.black_states,
      customWhiteStateColors: tokenPalette.common.white_states,
    },
    primary: {
      main: tokenPalette.primary.main,
      dark: tokenPalette.primary.dark,
      light: tokenPalette.primary.light,
      contrastText: tokenPalette.primary.contrastText,
      customStateColor: tokenPalette.primary._states,
    },
    secondary: {
      main: tokenPalette.secondary.main,
      dark: tokenPalette.secondary.dark,
      light: tokenPalette.secondary.light,
      contrastText: tokenPalette.secondary.contrastText,
      customStateColor: tokenPalette.secondary._states,
    },
    background: {
      default: tokenPalette.background["default-elevation"], // Card background
      paper: tokenPalette.background["default-elevation"],
      "default-elevation": tokenPalette.background["default-elevation"],
      "paper-elevation-0": tokenPalette.background["paper-elevation-0"],
      "paper-elevation-1": tokenPalette.background["paper-elevation-1"],
      "paper-elevation-2": tokenPalette.background["paper-elevation-2"],
      "paper-elevation-3": tokenPalette.background["paper-elevation-3"],
      "paper-elevation-4": tokenPalette.background["paper-elevation-4"],
      "paper-elevation-5": tokenPalette.background["paper-elevation-5"],
      "paper-elevation-6": tokenPalette.background["paper-elevation-6"],
      "paper-elevation-7": tokenPalette.background["paper-elevation-7"],
      "paper-elevation-8": tokenPalette.background["paper-elevation-8"],
      "paper-elevation-9": tokenPalette.background["paper-elevation-9"],
      "paper-elevation-10": tokenPalette.background["paper-elevation-10"],
      "paper-elevation-11": tokenPalette.background["paper-elevation-11"],
      "paper-elevation-12": tokenPalette.background["paper-elevation-12"],
      "paper-elevation-reversed":
        tokenPalette.background["paper-elevation-reversed"],
      "paper-depression": tokenPalette.background["paper-depression"],
      shround: tokenPalette.background.shround,
    },
    text: {
      primary: tokenPalette.text.primary, // Foundation/Primary/raisin-black
      secondary: tokenPalette.text.secondary,
      disabled: tokenPalette.text.disabled,
      contrastText: tokenPalette.text.contrastText,
      customStateColor: tokenPalette.text._states,
    },

    alert: {
      info: tokenPalette.info.light,
      success: tokenPalette.success.light,
      warning: tokenPalette.warning.light,
      error: tokenPalette.error.light,
    },
    action: tokenPalette.action,

    error: {
      main: tokenPalette.error.main,
      dark: tokenPalette.error.dark,
      light: tokenPalette.error.light,
      contrastText: tokenPalette.error.contrastText,
      bgContainer: tokenPalette.error.bgContainer,
      customStateColor: tokenPalette.error._states,
    },
    warning: {
      main: tokenPalette.warning.main,
      dark: tokenPalette.warning.dark,
      light: tokenPalette.warning.light,
      contrastText: tokenPalette.warning.contrastText,
      bgContainer: tokenPalette.warning.bgContainer,
      customStateColor: tokenPalette.warning._states,
    },
    info: {
      main: tokenPalette.info.main,
      dark: tokenPalette.info.dark,
      light: tokenPalette.info.light,
      contrastText: tokenPalette.info.contrastText,
      bgContainer: tokenPalette.info.bgContainer,
      customStateColor: tokenPalette.info._states,
    },
    success: {
      main: tokenPalette.success.main,
      dark: tokenPalette.success.dark,
      light: tokenPalette.success.light,
      contrastText: tokenPalette.success.contrastText,
      bgContainer: tokenPalette.success.bgContainer,
      customStateColor: tokenPalette.success._states,
    },
    tokens: {
      common: tokenPalette.common,
      outlines: tokenPalette.outlines,
      elevation: tokenPalette.elevation,
      icon: tokenPalette.icon,
    },
    seedType: {
      product: {
        main: materialColors.violet[400],
        contrastText: materialColors.onyx[50],
      },
      brand: {
        main: materialColors.amber[600],
        contrastText: materialColors.onyx[600],
      },
      photography: {
        main: materialColors.blue[400],
        contrastText: materialColors.onyx[50],
      },
      composition: {
        main: materialColors.green[400],
        contrastText: materialColors.onyx[50],
      },
      scene: {
        main: materialColors.amber[400],
        contrastText: materialColors.onyx[600],
      },
      casting: {
        main: materialColors.pink[400],
        contrastText: materialColors.onyx[50],
      },
    },
  },
  typography: {
    // Base font size/family for all MUI typography
    htmlFontSize: tokenTypography.baseFontSize,
    fontFamily: typographyFamily,

    // Font weight and size scale are custom if we want to use them
    fontWeightScale: tokenTypography.fontWeight,
    fontSizeScale: tokenTypography.fontSize,

    h1: {
      fontFeatureSettings: "'liga' off, 'clig' off",
      fontSize: `${typographyFontSize["6rem"]}px`,
      fontStyle: "normal",
      fontWeight: typographyFontWeight.light,
      lineHeight: "116.7%",
      letterSpacing: "-1.5px",
    },
    h2: {
      fontFeatureSettings: "'liga' off, 'clig' off",
      fontSize: `${typographyFontSize["3.75rem"]}px`,
      fontStyle: "normal",
      fontWeight: typographyFontWeight.light,
      lineHeight: "120%",
      letterSpacing: "-0.5px",
    },
    h3: {
      fontFeatureSettings: "'liga' off, 'clig' off",
      fontSize: `${typographyFontSize["3rem"]}px`,
      fontStyle: "normal",
      fontWeight: typographyFontWeight.regular,
      lineHeight: "116.7%",
    },
    h4: {
      fontFeatureSettings: "'liga' off, 'clig' off",
      fontSize: `${typographyFontSize["2.125rem"]}px`,
      fontStyle: "normal",
      fontWeight: typographyFontWeight.medium,
      lineHeight: "123.5%",
      letterSpacing: "0.25px",
    },
    h5: {
      fontFeatureSettings: "'liga' off, 'clig' off",
      fontSize: `${typographyFontSize["1.5rem"]}px`,
      fontStyle: "normal",
      fontWeight: typographyFontWeight.semiBold,
      lineHeight: "133.4%",
    },
    h6: {
      fontFeatureSettings: "'liga' off, 'clig' off",
      fontSize: `${typographyFontSize["1.25rem"]}px`,
      fontStyle: "normal",
      fontWeight: typographyFontWeight.semiBold,
      lineHeight: "160%",
      letterSpacing: "0.15px",
    },
    subtitle1: {
      fontFeatureSettings: "'liga' off, 'clig' off",
      fontSize: `${typographyFontSize["1rem"]}px`,
      fontStyle: "normal",
      fontWeight: typographyFontWeight.semiBold,
      lineHeight: "175%",
      letterSpacing: "0.15px",
    },
    subtitle2: {
      fontFeatureSettings: "'liga' off, 'clig' off",
      fontSize: `${typographyFontSize["0.75rem"]}px`,
      fontStyle: "normal",
      fontWeight: typographyFontWeight.semiBold,
      lineHeight: "157%",
      letterSpacing: "0.1px",
    },
    subtitle3: {
      fontFeatureSettings: "'liga' off, 'clig' off",
      fontSize: `${typographyFontSize["0.75rem"]}px`,
      fontStyle: "normal",
      fontWeight: typographyFontWeight.medium,
      lineHeight: "157%",
      letterSpacing: "0.1px",
    },
    body1: {
      fontFeatureSettings: "'liga' off, 'clig' off",
      fontSize: `${typographyFontSize["0.875rem"]}px`,
      fontStyle: "normal",
      fontWeight: typographyFontWeight.regular,
      lineHeight: "150%",
      letterSpacing: "0.15px",
    },
    body2: {
      fontFeatureSettings: "'liga' off, 'clig' off",
      fontSize: `${typographyFontSize["0.75rem"]}px`,
      fontStyle: "normal",
      fontWeight: typographyFontWeight.regular,
      lineHeight: "143%",
      letterSpacing: "0.17px",
    },
    body3: {
      fontFeatureSettings: "'liga' off, 'clig' off",
      fontSize: `${typographyFontSize["0.625rem"]}px`,
      fontStyle: "normal",
      fontWeight: typographyFontWeight.regular,
      lineHeight: "143%",
      letterSpacing: "0.17px",
    },
    // button1 in Orion theme
    button: {
      fontFeatureSettings: "'liga' off, 'clig' off",
      fontSize: `${typographyFontSize["0.875rem"]}px`,
      fontStyle: "normal",
      fontWeight: typographyFontWeight.medium,
      lineHeight: "143%",
      letterSpacing: "0.17px",
    },
    button2: {
      fontFeatureSettings: "'liga' off, 'clig' off",
      fontSize: `${typographyFontSize["0.75rem"]}px`,
      fontStyle: "normal",
      fontWeight: typographyFontWeight.medium,
      lineHeight: "143%",
      letterSpacing: "0.17px",
    },
    caption: {
      fontFeatureSettings: "'liga' off, 'clig' off",
      fontSize: `${typographyFontSize["0.75rem"]}px`,
      fontStyle: "normal",
      fontWeight: typographyFontWeight.regular,
      lineHeight: "166%",
      letterSpacing: "0.4px",
    },
    overline: {
      fontFeatureSettings: "'liga' off, 'clig' off",
      fontSize: `${typographyFontSize["0.75rem"]}px`,
      fontStyle: "normal",
      fontWeight: typographyFontWeight.regular,
      lineHeight: "266%",
      letterSpacing: "1px",
      textTransform: "uppercase",
    },
    chipLabel: {
      fontFeatureSettings: "'liga' off, 'clig' off",
      fontSize: `${typographyFontSize["0.8125rem"]}px`,
      fontStyle: "normal",
      fontWeight: typographyFontWeight.regular,
      lineHeight: "18px",
      letterSpacing: "1px",
    },
  },
  spacing: [0, ...Object.values(tokenSpacing)],
  shape: {
    // base border radius when used with MUI
    borderRadius: tokenShape.borderRadius.sm,

    // Below are border custom values from figma
    borderRadiusSm: tokenShape.borderRadius.sm,
    borderRadiusMd: tokenShape.borderRadius.md,
    borderRadiusLg: tokenShape.borderRadius.lg,
    borderRadiusButtonCorner: tokenShape.borderRadius.buttonCorner,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarColor: `${tokenPalette._native["scrollbar-bg"]} transparent`,
          "&::-webkit-scrollbar": {
            width: "8px",
            height: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: tokenPalette._native["scrollbar-bg"],
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ ownerState, theme }) => {
          const elevation = ownerState.elevation ?? 1;
          const backgroundColor = (() => {
            switch (elevation) {
              case 0:
                return theme.palette.background["default-elevation"];
              case 1:
                return theme.palette.background["paper-elevation-1"];
              case 2:
                return theme.palette.background["paper-elevation-3"];
              case 3:
                return theme.palette.background["paper-elevation-3"];
              case 4:
                return theme.palette.background["paper-elevation-4"];
              case 5:
                return theme.palette.background["paper-elevation-4"];
              case 6:
                return theme.palette.background["paper-elevation-5"];
              case 7:
                return theme.palette.background["paper-elevation-5"];
              case 8:
                return theme.palette.background["paper-elevation-6"];
              case 9:
                return theme.palette.background["paper-elevation-6"];
              case 10:
                return theme.palette.background["paper-elevation-7"];
              case 11:
                return theme.palette.background["paper-elevation-7"];
              case 12:
                return theme.palette.background["paper-elevation-8"];
              case 13:
                return theme.palette.background["paper-elevation-8"];
              case 14:
                return theme.palette.background["paper-elevation-9"];
              case 15:
                return theme.palette.background["paper-elevation-9"];
              case 16:
                return theme.palette.background["paper-elevation-10"];
              case 17:
                return theme.palette.background["paper-elevation-10"];
              case 18:
                return theme.palette.background["paper-elevation-10"];
              case 19:
                return theme.palette.background["paper-elevation-10"];
              case 20:
                return theme.palette.background["paper-elevation-10"];
              case 21:
                return theme.palette.background["paper-elevation-10"];
              case 22:
                return theme.palette.background["paper-elevation-11"];
              case 23:
                return theme.palette.background["paper-elevation-12"];
              case 24:
                return theme.palette.background["paper-elevation-12"];
              default:
                return theme.palette.background["paper-elevation-3"]; // Default to elevation 1
            }
          })();
          return {
            boxShadow: "none !important",
            backgroundColor,
          };
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "none !important",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: "none !important",
          backgroundColor: tokenPalette._components.appBar.defaultFill,
          color: tokenPalette.text.primary,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          boxShadow: "none !important",
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          boxShadow: "none !important",
        },
      },
    },
    MuiMenu: {
      defaultProps: {
        elevation: 0,
        disableAutoFocusItem: true,
      },
      styleOverrides: {
        // MenuList uses MuiList, which defaults to paddingTop/Bottom 8px — removes gap so selection meets paper edges.
        list: {
          paddingTop: 0,
          paddingBottom: 0,
        },
        paper: {
          backgroundColor: tokenPalette.background["paper-elevation-reversed"],
          border: `1px solid ${materialColors.onyx[400]}`,
          borderRadius: `${tokenShape.borderRadius.md}px`,
          marginTop: `${tokenSpacing[2]}px`,
          overflowX: "hidden",
          overflowY: "auto",
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          backgroundColor: "transparent",
          color: tokenPalette.text.contrastText,
          "&:hover, &:focus": {
            backgroundColor: materialColorsOpacity.onyx["50-24%"],
          },
          "&.Mui-selected": {
            backgroundColor: tokenPalette.primary.main,
            color: tokenPalette.primary.contrastText,
          },
          "&.Mui-selected:hover, &.Mui-selected:focus": {
            backgroundColor: tokenPalette.primary.light,
          },
          "& .MuiListItemIcon-root": {
            color: tokenPalette.icon.contrastIcon,
          },
          "&.Mui-selected .MuiListItemIcon-root": {
            color: tokenPalette.primary.contrastText,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.background.default,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: materialColors.onyx[400],
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: materialColors.onyx[300],
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.primary.main,
          },
        }),
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          boxShadow: "none !important",
          backgroundColor: tokenPalette._components.tooltip.fill,
          "&.MuiTooltip-tooltipPlacementTop": { margin: "4px 0 !important" },
          "&.MuiTooltip-tooltipPlacementBottom": {
            margin: "4px 0 !important",
          },
          "&.MuiTooltip-tooltipPlacementLeft": { margin: "0 4px !important" },
          "&.MuiTooltip-tooltipPlacementRight": { margin: "0 4px !important" },
        },
        arrow: {
          color: tokenPalette._components.tooltip.fill,
        },
      },
    },
    MuiDialog: {
      defaultProps: {
        slotProps: {
          paper: {
            elevation: 0,
          },
        },
      },
      styleOverrides: {
        paper: ({ theme }) => {
          return {
            boxShadow: "none !important",
            backgroundColor: theme.palette.common.white,
          };
        },
      },
    },
    MuiListSubheader: {
      styleOverrides: {
        root: ({ theme }) => {
          return {
            backgroundColor: theme.palette.background["paper-elevation-1"],
          };
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: `${tokenShape.borderRadius.buttonCorner}px`,
          padding: `${tokenSpacing[3]}px 22px`,
          boxShadow: "none !important",
        },
      },
    },
    MuiAlert: {
      defaultProps: {
        iconMapping: {
          success: createElement(CheckCircleOutlineIcon, {
            fontSize: "inherit",
          }),
          info: createElement(InfoOutlinedIcon, { fontSize: "inherit" }),
          warning: createElement(WarningAmberIcon, { fontSize: "inherit" }),
          error: createElement(ErrorOutlineIcon, { fontSize: "inherit" }),
        },
      },
      styleOverrides: {
        root: {
          boxShadow: "none !important",
          borderRadius: "10px",
          display: "flex",
          minHeight: "46px",
          padding: "6px 20px",
          alignItems: "center",
          gap: "10px",
          flexShrink: 0,
          alignSelf: "stretch",
          fontFamily: typographyFamily,
          fontSize: `${tokenTypography.baseFontSize}px`,
          fontStyle: "normal",
          fontWeight: tokenTypography.fontWeight.regular,
          lineHeight: "normal",
        },
        standardError: ({ theme }) => ({
          backgroundColor: theme.palette.alert.error,
          color: theme.palette.error.dark,
        }),
        standardSuccess: ({ theme }) => ({
          backgroundColor: theme.palette.alert.success,
          color: theme.palette.success.dark,
        }),
        standardInfo: ({ theme }) => ({
          backgroundColor: theme.palette.alert.info,
          color: theme.palette.info.dark,
        }),
        standardWarning: ({ theme }) => ({
          backgroundColor: theme.palette.alert.warning,
          color: theme.palette.warning.contrastText,
          "& .MuiAlert-icon": {
            color: theme.palette.warning.contrastText,
          },
        }),
        outlinedError: ({ theme }) => ({
          color: theme.palette.error.main,
          borderColor: theme.palette.error.main,
          "& .MuiAlert-icon": {
            color: theme.palette.error.main,
          },
        }),
        outlinedSuccess: ({ theme }) => ({
          color: theme.palette.success.main,
          borderColor: theme.palette.success.main,
          "& .MuiAlert-icon": {
            color: theme.palette.success.main,
          },
        }),
        outlinedInfo: ({ theme }) => ({
          color: theme.palette.info.main,
          borderColor: theme.palette.info.main,
          "& .MuiAlert-icon": {
            color: theme.palette.info.main,
          },
        }),
        outlinedWarning: ({ theme }) => ({
          color: theme.palette.warning.main,
          borderColor: theme.palette.warning.main,
          "& .MuiAlert-icon": {
            color: theme.palette.warning.main,
          },
        }),
        filledError: ({ theme }) => ({
          backgroundColor: theme.palette.alert.error,
          color: theme.palette.error.dark,
          "& .MuiAlert-icon": {
            color: theme.palette.error.dark,
          },
        }),
        filledSuccess: ({ theme }) => ({
          backgroundColor: theme.palette.alert.success,
          color: theme.palette.success.dark,
          "& .MuiAlert-icon": {
            color: theme.palette.success.dark,
          },
        }),
        filledInfo: ({ theme }) => ({
          backgroundColor: theme.palette.alert.info,
          color: theme.palette.info.dark,
          "& .MuiAlert-icon": {
            color: theme.palette.info.dark,
          },
        }),
        filledWarning: ({ theme }) => ({
          backgroundColor: theme.palette.alert.warning,
          color: theme.palette.warning.contrastText,
          "& .MuiAlert-icon": {
            color: theme.palette.warning.contrastText,
          },
        }),
        icon: {
          fontSize: `${tokenTypography.fontSize["1.25rem"]}px`,
          opacity: 1,
          // Root supplies the gap; MUI's default icon margin would double it.
          marginRight: 0,
        },
        action: {
          padding: 0,
        },
      },
    },
    MuiChip: {
      defaultProps: {
        deleteIcon: createElement(CancelIcon),
      },
      styleOverrides: {
        root: () => ({
          fontFamily: typographyFamily,
          fontSize: `${tokenTypography.fontSize["0.75rem"]}px`,
          fontStyle: "normal",
          fontWeight: tokenTypography.fontWeight.regular,
          lineHeight: "normal",
          padding: "6px 12px",
          borderRadius: `${tokenShape.borderRadius.sm}px`,
          height: "auto",
          boxShadow: "none",
          "& .MuiChip-label": {
            padding: "0",
            fontSize: `${tokenTypography.fontSize["0.75rem"]}px`,
            fontWeight: tokenTypography.fontWeight.regular,
          },
          "& .MuiChip-deleteIcon": {
            fontSize: `${tokenTypography.baseFontSize}px`,
            margin: "0 0 0 8px",
            backgroundColor: "transparent",
            borderRadius: `${tokenShape.borderRadius.none}px`,
            "&:hover": {
              opacity: 0.7,
              backgroundColor: "transparent",
            },
          },
        }),
        /** Filled only (`variant="filled"`). Skips `lightGray` and `outlined`, which use their own classes. */
        filled: () => ({
          border: "none",
        }),
        outlined: () => ({
          backgroundColor: "transparent",
          border: "1px solid currentColor",
        }),
        sizeSmall: () => ({
          padding: "3px 4px",
          "& .MuiChip-label": {
            padding: "0 6px",
          },
        }),
      },
      variants: [
        {
          props: { variant: "filled", color: "default" },
          style: ({ theme }) => ({
            backgroundColor: materialColors.onyx[600],
            color: theme.palette.text.contrastText,
            "& .MuiChip-label": {
              color: theme.palette.text.contrastText,
            },
            "& .MuiChip-deleteIcon": {
              color: theme.palette.text.contrastText,
              "&:hover": {
                color: theme.palette.text.contrastText,
              },
            },
            "&.MuiChip-clickable:hover": {
              backgroundColor: materialColorsOpacity.onyx["600-12%"],
            },
            "&:focus": {
              backgroundColor: materialColorsOpacity.onyx["600-24%"],
            },
          }),
        },
        {
          props: { variant: "outlined", color: "default" },
          style: ({ theme }) => ({
            color: theme.palette.text.primary,
            "& .MuiChip-label": {
              color: "inherit",
            },
            "& .MuiChip-deleteIcon": {
              color: theme.palette.text.secondary,
              "&:hover": {
                color: theme.palette.text.primary,
              },
            },
            "&.MuiChip-clickable:hover": {
              backgroundColor: theme.palette.action.hover,
            },
            "&.MuiChip-clickable.Mui-focusVisible": {
              backgroundColor: theme.palette.action.focus,
            },
          }),
        },
        {
          props: { variant: "lightGray" },
          style: ({ theme }) => ({
            backgroundColor: theme.palette.background["paper-elevation-2"],
            "& .MuiChip-label": {
              color: theme.palette.text.primary,
            },
            "&.MuiChip-clickable:hover": {
              backgroundColor: theme.palette.background["paper-elevation-2"],
            },
            "& .MuiChip-deleteIcon": {
              color: materialColorsOpacity.onyx["600-40%"],
            },
            "& .MuiChip-deleteIcon:hover": {
              color: theme.palette.text.primary,
            },
          }),
        },
      ],
    },
    MuiCheckbox: {
      defaultProps: {
        icon: createElement(CheckBoxOutlineBlankIcon),
        checkedIcon: createElement(CheckBoxIcon),
        indeterminateIcon: createElement(IndeterminateCheckBoxIcon),
      },
    },
    MuiSelect: {
      defaultProps: {
        IconComponent: ExpandMoreIcon,
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        popupIcon: createElement(ExpandMoreIcon),
        clearIcon: createElement(CloseIcon, { fontSize: "small" }),
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: ({ theme }) => ({
          height: 6,
          borderRadius: 100,
          backgroundColor: theme.palette.background["paper-elevation-2"],
        }),
        bar: {
          borderRadius: 100,
        },
      },
      variants: [
        {
          // Custom, animated multicolor bar resembling the design screenshot
          props: { variant: "orion" } as const,
          style: () => ({
            height: 6,
            borderRadius: 100,
            overflow: "hidden",
            "& .MuiLinearProgress-bar": {
              borderRadius: 100,
              backgroundImage:
                "linear-gradient(90deg, #4AF731 0% 4%, #F16060 4% 6%,#7700EE 6% 9%,#F98544 9% 12%,#F5F0F9 12% 100%)",
              backgroundSize: "200% 200%",
              animation: "mosaicShift 2.4s cubic-bezier(0.5, 0, 0, 1) infinite",
            },
            "@keyframes mosaicShift": {
              "0%": { backgroundPosition: "100%" },
              "100%": { backgroundPosition: "-100% 0%" },
            },
          }),
        },
      ],
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: tokenPalette._components.backdrop.fill,
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
        },
        // Popover/Menu use an invisible backdrop; clear blur so menus aren't frosted.
        invisible: {
          backgroundColor: "transparent",
          backdropFilter: "none",
          WebkitBackdropFilter: "none",
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: {
          maxWidth: "100%",
          overflow: "hidden",
          "& .MuiBreadcrumbs-ol": { flexWrap: "nowrap" },
          "& .MuiBreadcrumbs-separator": { flexShrink: 0 },
          "& .MuiBreadcrumbs-li": { minWidth: 0 },
          "& .MuiBreadcrumbs-li:not(:last-of-type)": { flexShrink: 0 },
          "& .MuiBreadcrumbs-li:last-of-type": {
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
          },
        },
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          backgroundColor: tokenPalette._components.snackbar.fill,
        },
      },
    },
    MuiRating: {
      styleOverrides: {
        iconEmpty: {
          color: tokenPalette._components.rating.enabledBorder,
        },
        iconFilled: {
          color: tokenPalette._components.rating.activeFill,
        },
      },
    },
    MuiStepConnector: {
      styleOverrides: {
        line: () => ({
          borderColor: tokenPalette._components.stepper.connector,
        }),
      },
    },
    MuiSkeleton: {
      variants: [
        {
          props: { variant: "queen" },
          style: ({ theme }) => ({
            backgroundColor: theme.palette.background["paper-elevation-4"],
            "&::after": {
              background: `linear-gradient(90deg, transparent, ${theme.palette.background["paper-elevation-2"]}, transparent)`,
            },
          }),
        },
      ],
    },
    MuiPickerPopper: {
      styleOverrides: {
        paper: {
          backgroundColor: pickerContainerBackground,
          border: `1px solid ${pickerBorderColor}`,
          borderRadius: `${tokenShape.borderRadius.lg}px`,
          boxShadow: "none",
          "& .MuiDivider-root": {
            display: "none",
          },
          // Keep DateTimePicker clock items aligned with calendar styling.
          "& .MuiDigitalClock-item.MuiMenuItem-root, & .MuiMultiSectionDigitalClockSection-item":
            {
              color: tokenPalette.text.primary,
              borderRadius: `${tokenShape.borderRadius.md}px`,
              fontSize: `${tokenTypography.fontSize["0.75rem"]}px`,
              fontWeight: tokenTypography.fontWeight.regular,
              letterSpacing: "0.17px",
            },
          "& .MuiDigitalClock-item.MuiMenuItem-root:hover, & .MuiMultiSectionDigitalClockSection-item:hover":
            {
              backgroundColor: tokenPalette.action.hover,
            },
          "& .MuiDigitalClock-item.Mui-selected, & .MuiMultiSectionDigitalClockSection-item.Mui-selected":
            {
              backgroundColor: tokenPalette.primary.main,
              color: tokenPalette.primary.contrastText,
            },
          "& .MuiDigitalClock-item.Mui-selected:hover, & .MuiMultiSectionDigitalClockSection-item.Mui-selected:hover":
            {
              backgroundColor: tokenPalette.primary.main,
            },
          "& .MuiDigitalClock-item.Mui-disabled, & .MuiMultiSectionDigitalClockSection-item.Mui-disabled":
            {
              color: tokenPalette.text.disabled,
            },
        },
      },
    },
    MuiPickersLayout: {
      styleOverrides: {
        root: {
          backgroundColor: pickerContainerBackground,
          borderRadius: `${tokenShape.borderRadius.lg}px`,
          overflow: "hidden",
        },
      },
    },
    MuiPickersToolbar: {
      styleOverrides: {
        root: {
          backgroundColor: pickerContainerBackground,
          padding: `${tokenSpacing[5]}px ${tokenSpacing[7]}px`,
        },
      },
    },
    MuiDateTimePickerToolbar: {
      styleOverrides: {
        root: {
          backgroundColor: pickerContainerBackground,
          padding: `${tokenSpacing[5]}px ${tokenSpacing[7]}px`,
        },
      },
    },
    MuiPickersCalendarHeader: {
      styleOverrides: {
        root: {
          color: tokenPalette.text.primary,
          marginTop: 0,
          marginBottom: 0,
          padding: `${tokenSpacing[5]}px ${tokenSpacing[4]}px ${tokenSpacing[3]}px ${tokenSpacing[7]}px`,
        },
        labelContainer: {
          display: "flex",
          alignItems: "center",
          gap: tokenSpacing[6],
        },
        label: {
          color: tokenPalette.text.primary,
          fontSize: `${tokenTypography.fontSize["1rem"]}px`,
          fontWeight: tokenTypography.fontWeight.medium,
          letterSpacing: "0.15px",
          lineHeight: "1.5",
        },
        switchViewButton: {
          color: tokenPalette.text.primary,
          padding: "5px",
          borderRadius: `${tokenShape.borderRadius.buttonCorner}px`,
        },
      },
    },
    MuiPickersArrowSwitcher: {
      styleOverrides: {
        root: {
          gap: tokenSpacing[3],
        },
        button: {
          color: tokenPalette.text.primary,
          padding: "5px",
          borderRadius: `${tokenShape.borderRadius.buttonCorner}px`,
        },
      },
    },
    MuiDateCalendar: {
      styleOverrides: {
        root: {
          margin: 0,
          width: "100%",
          maxHeight: "none",
        },
      },
    },
    MuiDayCalendar: {
      styleOverrides: {
        root: {
          padding: `${tokenSpacing[4]}px ${tokenSpacing[5]}px ${tokenSpacing[3]}px`,
        },
        weekContainer: {
          margin: 0,
        },
        weekDayLabel: {
          color: tokenPalette.text.secondary,
          width: 36,
          height: 36,
          fontSize: `${tokenTypography.fontSize["0.75rem"]}px`,
          fontWeight: tokenTypography.fontWeight.regular,
          letterSpacing: "0.17px",
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: {
          width: 36,
          height: 36,
          borderRadius: `${tokenShape.borderRadius.md}px`,
          margin: "1px",
          border: "1px solid transparent",
          color: tokenPalette.text.primary,
          fontSize: `${tokenTypography.fontSize["0.75rem"]}px`,
          fontWeight: tokenTypography.fontWeight.regular,
          letterSpacing: "0.17px",
          "&:hover": {
            backgroundColor: tokenPalette.action.hover,
          },
          "&.Mui-selected": {
            backgroundColor: tokenPalette.primary.main,
            color: tokenPalette.primary.contrastText,
            border: "1px solid transparent",
          },
          "&.Mui-selected:hover, &.Mui-selected:focus": {
            backgroundColor: tokenPalette.primary.main,
          },
          "&.MuiPickersDay-today:not(.Mui-selected)": {
            borderColor: tokenPalette.action.active,
            backgroundColor: "transparent",
          },
          "&.Mui-disabled": {
            color: tokenPalette.text.disabled,
          },
        },
      },
    },
  },
});

export default orionDesignSystemTheme;
